import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { shortDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Badge, Button, PageHeader, EmptyState, Input } from "@/components/kit";
import { Warning, Check, X as XIcon, ArrowRight } from "@phosphor-icons/react";

const TABS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export default function EditRequestsPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [noteFor, setNoteFor] = useState({}); // id -> note

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/edit-requests?status=${status}`);
      setItems(data);
    } catch { setItems([]); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const isSuperAdmin = user.role === "super_admin";

  const act = async (id, kind) => {
    setBusy(id + kind);
    try {
      await api.post(`/edit-requests/${id}/${kind}`, { note: noteFor[id] || null });
      setNoteFor((prev) => { const n = { ...prev }; delete n[id]; return n; });
      window.dispatchEvent(new Event("edit-requests-changed"));
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy("");
    }
  };

  const cancelOwn = async (id) => {
    if (!window.confirm("Cancel this request?")) return;
    setBusy(id + "cancel");
    try {
      await api.delete(`/edit-requests/${id}`);
      window.dispatchEvent(new Event("edit-requests-changed"));
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <PageHeader
        title="Edit requests"
        subtitle={isSuperAdmin
          ? "Approve or reject changes proposed by branch staff and agents."
          : "Track edit requests you have raised."}
      />

      <Card className="p-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setStatus(t.value)}
              data-testid={`er-tab-${t.value}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                status === t.value
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Card>

      {items.length === 0 ? (
        <Card>
          <EmptyState title={`No ${status} requests`} />
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <Card key={r.id} className="p-5" data-testid={`er-item-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <Warning size={18} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/customers/${r.customer_id}`}
                        className="font-semibold text-slate-900 hover:text-emerald-700"
                        data-testid={`er-cust-link-${r.id}`}
                      >
                        {r.customer_name || "(customer)"}
                        <ArrowRight size={12} className="inline ml-1" />
                      </Link>
                      <StatusPill status={r.status} />
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      by <span className="font-medium">{r.requested_by_name || "user"}</span>
                      {r.requested_by_role && <span className="text-slate-400"> · {r.requested_by_role}</span>}
                      {" · raised "}{shortDate(r.created_at)}
                    </div>
                    {r.reason && <div className="text-sm text-slate-700 mt-2 italic">“{r.reason}”</div>}

                    <div className="mt-3 space-y-1.5">
                      {Object.keys(r.diff || {}).map((k) => (
                        <div key={k} className="text-sm">
                          <span className="font-medium capitalize">{k.replace(/_/g, " ")}</span>
                          <div className="mt-0.5 pl-2 border-l-2 border-slate-200 text-xs">
                            <div className="text-rose-700 whitespace-pre-wrap">− {formatValue(r.diff[k].old)}</div>
                            <div className="text-emerald-700 whitespace-pre-wrap">+ {formatValue(r.diff[k].new)}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {r.status !== "pending" && (
                      <div className="text-xs text-slate-500 mt-3">
                        {r.status === "approved" ? "Approved" : "Rejected"} by {r.reviewed_by_name}
                        {" on "}{shortDate(r.reviewed_at)}
                        {r.review_note && <> — “{r.review_note}”</>}
                      </div>
                    )}

                    {r.status === "pending" && isSuperAdmin && (
                      <div className="mt-4 flex flex-wrap gap-2 items-center">
                        <Input
                          className="flex-1 min-w-[200px]"
                          placeholder="Review note (optional)"
                          value={noteFor[r.id] || ""}
                          onChange={(e) => setNoteFor((p) => ({ ...p, [r.id]: e.target.value }))}
                          testId={`er-note-${r.id}`}
                        />
                        <Button
                          onClick={() => act(r.id, "approve")}
                          disabled={busy === r.id + "approve"}
                          data-testid={`er-approve-${r.id}`}
                        >
                          <Check size={14} weight="bold" /> Approve
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => act(r.id, "reject")}
                          disabled={busy === r.id + "reject"}
                          data-testid={`er-reject-${r.id}`}
                        >
                          <XIcon size={14} weight="bold" /> Reject
                        </Button>
                      </div>
                    )}
                    {r.status === "pending" && !isSuperAdmin && r.requested_by === user.id && (
                      <button
                        onClick={() => cancelOwn(r.id)}
                        data-testid={`er-cancel-${r.id}`}
                        className="text-xs text-slate-500 underline hover:text-rose-700 mt-3"
                      >
                        Cancel this request
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const tone =
    status === "pending" ? "warning"
    : status === "approved" ? "success"
    : "danger";
  return <Badge tone={tone}>{status}</Badge>;
}

function formatValue(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) {
    return v.map((n) => {
      if (!n || typeof n !== "object") return String(n);
      const parts = [n.name];
      if (n.relation) parts.push(`(${n.relation})`);
      if (n.share_pct != null) parts.push(`${n.share_pct}%`);
      return parts.filter(Boolean).join(" ");
    }).join("; ") || "—";
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
